const rawSiteUrl = (
  process.env.REACT_APP_SITE_URL ||
  process.env.SITE_URL ||
  'https://makayaenfo.com'
).trim();

export const SITE_URL = rawSiteUrl.replace(/\/+$/, '') || 'https://makayaenfo.com';
export const SITE_NAME = 'Makaya Enfo';
export const DEFAULT_DESCRIPTION =
  "Makaya Enfo est un portail d'information haïtien avec actualités, analyses, reportages, podcasts et couverture en temps réel.";
export const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;

function plainText(value) {
  return String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function truncate(value, maxLength) {
  const text = plainText(value);
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1).trim()}...`;
}

function upsertMeta(attribute, key, content) {
  if (typeof document === 'undefined') return;
  const value = String(content || '').trim();
  let el = document.querySelector(`meta[${attribute}="${key}"]`);
  if (!value) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attribute, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function upsertLink(rel, href) {
  if (typeof document === 'undefined') return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!href) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(id, data) {
  if (typeof document === 'undefined') return;
  let el = document.getElementById(id);
  if (!data) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function buildCanonicalUrl(path = '/') {
  const raw = String(path || '/').trim() || '/';
  if (/^https?:\/\//i.test(raw)) return raw;
  const normalizedPath = raw.startsWith('/') ? raw : `/${raw}`;
  return new URL(normalizedPath, `${SITE_URL}/`).toString();
}

export function formatSeoTitle(title) {
  const cleanTitle = truncate(title, 180);
  if (!cleanTitle) return SITE_NAME;
  return cleanTitle.toLowerCase().includes('makaya enfo')
    ? cleanTitle
    : `${cleanTitle} | ${SITE_NAME}`;
}

export function applySeo({
  title = SITE_NAME,
  description = DEFAULT_DESCRIPTION,
  path = typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : '/',
  image = DEFAULT_IMAGE,
  type = 'website',
  noindex = false,
  keywords = [],
  publishedTime = '',
  modifiedTime = '',
  author = '',
  section = '',
  tags = [],
  jsonLd = null,
} = {}) {
  if (typeof document === 'undefined') return;

  const finalTitle = formatSeoTitle(title);
  const finalDescription = truncate(description || DEFAULT_DESCRIPTION, 170);
  const canonicalUrl = buildCanonicalUrl(path);
  const finalImage = image || DEFAULT_IMAGE;
  const robots = noindex
    ? 'noindex, nofollow'
    : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

  document.title = finalTitle;
  upsertMeta('name', 'description', finalDescription);
  upsertMeta('name', 'robots', robots);
  upsertMeta('name', 'googlebot', robots);
  upsertMeta('name', 'keywords', Array.isArray(keywords) ? keywords.filter(Boolean).join(', ') : keywords);

  upsertLink('canonical', noindex ? '' : canonicalUrl);

  upsertMeta('property', 'og:site_name', SITE_NAME);
  upsertMeta('property', 'og:locale', 'fr_HT');
  upsertMeta('property', 'og:type', type);
  upsertMeta('property', 'og:title', finalTitle);
  upsertMeta('property', 'og:description', finalDescription);
  upsertMeta('property', 'og:url', canonicalUrl);
  upsertMeta('property', 'og:image', finalImage);

  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', finalTitle);
  upsertMeta('name', 'twitter:description', finalDescription);
  upsertMeta('name', 'twitter:image', finalImage);

  upsertMeta('property', 'article:published_time', publishedTime);
  upsertMeta('property', 'article:modified_time', modifiedTime);
  upsertMeta('property', 'article:author', author);
  upsertMeta('property', 'article:section', section);

  document.querySelectorAll('meta[property="article:tag"]').forEach((el) => el.remove());
  (Array.isArray(tags) ? tags : []).filter(Boolean).slice(0, 12).forEach((tag) => {
    const el = document.createElement('meta');
    el.setAttribute('property', 'article:tag');
    el.setAttribute('content', String(tag));
    document.head.appendChild(el);
  });

  upsertJsonLd('seo-jsonld', jsonLd);
}
